import React from "react";
import { useLocation } from "react-router-dom";
import { Skeleton } from "~/components/ui";
import {
  Sidebar,
  SidebarContent, // SidebarFooter,
  SidebarHeader,
  SidebarRail,
  useSidebar,
} from "~/components/ui/sidebar";
import { DEFAULT_API_PAYLOAD } from "~/lib/constants";
import { restApi } from "~/redux/restApi";

// import { NavSecondary } from "./bottom";
import SidebarIcon from "./icon";
import { NavMain } from "./menu";

interface MenuItem {
  name: string;
  module: string;
  requiredPermissionKey: string[];
  requiredPermissionTypes: string[];
  children: MenuItem[];
  icon: string | null;
  route: string | null;
  order: number;
  isVisible: boolean;
}

export interface ModuleGroup {
  heading: string;
  menus: MenuItem[];
}

type GroupedByModule = ModuleGroup[];

function groupByModule(items: MenuItem[]): GroupedByModule {
  const moduleMap: { [module: string]: ModuleGroup } = {};

  items.forEach((item) => {
    let heading = item.module;
    if (item.module === "Inventory") {
      heading = "Inventory Management";
    } else if (item.module === "Register") {
      heading = "Compliance Registers";
    }

    if (!moduleMap[item.module]) {
      moduleMap[item.module] = {
        heading: heading,
        menus: [],
      };
    }
    moduleMap[item.module].menus.push(item);
  });

  return Object.values(moduleMap);
}

const getActiveModule = (pathname: string) => {
  if (pathname.startsWith("/hse")) return "hse";
  if (pathname.startsWith("/asset")) return "assets";
  if (pathname.startsWith("/contractors")) return "contractors";
  if (pathname.startsWith("/finance")) return "finance";
  if (pathname.startsWith("/daily-ops")) return "daily-ops";
  if (
    pathname.startsWith("/personnel") ||
    pathname.startsWith("/resource/employees")
  )
    return "personnel";
  if (
    pathname.startsWith("/resources") ||
    pathname.startsWith("/resource/inventory") ||
    pathname.startsWith("/resource/registers")
  )
    return "resources";
  return "hse";
};

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { data, isLoading, isFetching } = restApi.useGetSidebarPermissionsQuery(
    {
      ...DEFAULT_API_PAYLOAD,
    },
  );
  const permissions = data?.result as MenuItem[];
  const { isMobile } = useSidebar();
  const location = useLocation();
  const currentPath = location.pathname;

  const activeModule = getActiveModule(currentPath);

  // Filter permissions dynamically based on the active module using an inclusive allowlist
  const MODULE_MAPPING: Record<
    string,
    { allowedModules: string[]; allowedItems?: string[] }
  > = {
    hse: {
      allowedModules: [
        "General",
        "Item Management",
        "Compliance Management",
        "Record Management",
      ],
    },
    personnel: {
      allowedModules: ["Employees"],
    },
    resources: {
      allowedModules: ["Inventory", "Register"],
    },
    assets: {
      allowedModules: ["Asset Management"],
      allowedItems: ["Assets"],
    },
    contractors: { allowedModules: [] },
    finance: { allowedModules: [] },
    "daily-ops": { allowedModules: ["Daily Operations"] },
  };

  const config = MODULE_MAPPING[activeModule] || { allowedModules: [] };

  const filteredPermissions = (permissions ?? []).filter((item) => {
    const isModuleAllowed = config.allowedModules.includes(item.module);
    const isItemAllowed = config.allowedItems?.includes(item.name);
    return isModuleAllowed || isItemAllowed;
  });

  const showSidebarContent =
    activeModule === "hse" ||
    activeModule === "personnel" ||
    activeModule === "resources" ||
    activeModule === "assets" ||
    activeModule === "daily-ops";

  return (
    <Sidebar
      collapsible="icon"
      side={isMobile ? "right" : "left"}
      {...props}
      className="z-20 bg-primary-500"
    >
      <SidebarHeader>
        <SidebarIcon />
      </SidebarHeader>
      <SidebarContent className="no-scrollbar -mt-8 overflow-y-auto bg-primary-500 pb-24">
        {(isLoading || isFetching) && showSidebarContent && (
          <div className="mt-10">
            {Array.from({ length: 12 }).map((_, index) => (
              <Skeleton
                className="mx-auto my-6 w-10/12 rounded-2xl bg-neutral-700 py-5"
                key={index}
              />
            ))}
          </div>
        )}
        {showSidebarContent &&
          groupByModule(filteredPermissions)?.map((group, idx) => (
            <NavMain group={group} key={idx} />
          ))}
      </SidebarContent>

      <SidebarRail />
    </Sidebar>
  );
}
